/*
 * Copyright (c) 2020 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */

import { Inject, Injectable, Injector } from '@angular/core';
import { Logger, MessageService } from '@core/lib/services';
import { UserPreferenceService } from '@app/common/user-preference.service';
import { EventMessage, Events, EventService } from '@app/common/event';
import { ProxyDataService } from '@app/core/http/proxy-data.service';
import { appConstants } from '@app/common/app-constants';
import { RealTimeService } from '@app/common/real-time.service';
import { SsePatientImagingHistoryService } from '@app/ui/client-cache';
import { UtilService } from '@app/util/util.service';
import { InteractionLogService } from '@core/lib/log';
import { IntegrationStatusService } from '@app/di/integration-status.service';
import { DiConfigService } from '@app/di/di-config.service';
import { DOCUMENT } from '@angular/common';
import { PreAction, ViewerPreAction } from '@app/di/integrations/pre-action.decorator';

const log = new Logger('SignOutService');

@Injectable({
  providedIn: 'root'
})
export class SignOutService {
  private desktopIntegrationCleanupCallbacks = [];
  private signOutKey = false;
  private jwtTokenTimeout;
  private window: Window;
  clearAssignment = false;
  constructor(
    private userPreferenceService: UserPreferenceService,
    private eventService: EventService,
    private dataService: ProxyDataService,
    private realtimeService: RealTimeService,
    private messageService: MessageService,
    private ssePatientImagingHistoryService: SsePatientImagingHistoryService,
    private utilService: UtilService,
    private interactionLog: InteractionLogService,
    private integrationStatusService: IntegrationStatusService,
    private diConfigService: DiConfigService,
    @Inject(DOCUMENT) private document: any,
    public injector: Injector // Keep this injection as public, being used by ViewerPreAction
  ) {
    this.window = (this.document as Document).defaultView;
    this.window.addEventListener('beforeunload', this.onBeforeUnload);
    this.messageService.on('signOutOnSessionTimeout').subscribe(() => {
      this.signOut();
    });
  }

  onBeforeUnload = () => {
    if (this.utilService.isSharedExamUrl(this.window.location.href)) {
      log.debug('Bypassing onBeforeUnload on /shared-exam route');
      return;
    }
    log.info('onbeforeunload event occurred - Is user logged in? ' + window.localStorage.currentUserLoggedIn);
    if (localStorage.currentUserLoggedIn === 'true') {
      this.signOutOnBrowserClose();
    }
    this.window.removeEventListener('beforeunload', this.onBeforeUnload);
  };

  expireJwtToken() {
    if (this.jwtTokenTimeout) {
      clearTimeout(this.jwtTokenTimeout);
      this.jwtTokenTimeout = undefined;
    }

    return new Promise((resolve, reject) => {
      const params = { clearAssignments: false };
      if (this.clearAssignment) {
        params.clearAssignments = true;
      }
      this.dataService
        .httpGet(appConstants.appURLs.logout, { params })
        .then(() => {
          this.cleanupSubscriptions();
          this.redirectToLogin();
          resolve(undefined);
        })
        .catch(ex => {
          log.error('Error while calling logging out. Logout aborted ' + JSON.stringify(ex));
          reject();
        });
    });
  }

  @PreAction('close')
  @ViewerPreAction('logout')
  async signOut(clearAssignments?: boolean) {
    localStorage.currentUserLoggedIn = 'false';
    this.signOutKey = true;
    this.executeCleanup();
    this.userPreferenceService.saveAllUserPreferences();
    this.clearAssignment = clearAssignments;
    try {
      // send remaining interactionlog to server
      await this.interactionLog.sendLogsToServer();

      await new Promise((resolve, reject) => {
        this.eventService.sendEvent(new EventMessage(Events.logout, null)).then(
          () => {
            if (this.integrationStatusService.getUVLaunchStatus()) {
              this.jwtTokenTimeout = setTimeout(() => {
                this.expireJwtToken().catch(appConstants.noop);
              }, this.diConfigService.UVIEWER_CONFIG.PLUGIN_CLOSE_TIMEOUT);
              resolve(undefined);
            } else {
              this.expireJwtToken().catch(appConstants.noop);
              resolve(undefined);
            }
          },
          message => {
            log.error('Logout event unsuccessful.Logout aborted ' + message);
            reject();
          }
        );
      });
    } catch (error) {
      log.error('Exception occurred while logging out of the application', error);
    }
  }

  signOutOnBrowserClose() {
    log.debug('Signout on browser close initiated');
    localStorage.currentUserLoggedIn = 'false';
    if (!this.signOutKey) {
      this.executeCleanup();
      return this.expireJwtToken();
    }
  }

  /**
   * Method to automatically sign-out a User from RadDesktop.
   * The responsibility of this method is to:
   *  1. Execute integration cleanups.
   *  2. Save all user preferences
   *  3. Clear UV session information.
   *  4. Redirect to Login page.
   */
  @PreAction('close')
  @ViewerPreAction('logout')
  automaticSignOut() {
    this.signOutKey = true;
    this.executeCleanup();

    this.cleanupSubscriptions();
    this.redirectToLogin();
  }

  registerCallback(callback) {
    // If the callback function is already registered remove it and add the latest one to maintain
    // the execution order
    const index = this.desktopIntegrationCleanupCallbacks.indexOf(callback);
    if (index !== -1) {
      this.desktopIntegrationCleanupCallbacks.splice(index, 1);
    }
    this.desktopIntegrationCleanupCallbacks.push(callback);
  }

  executeCleanup() {
    log.info('executeCleanup - unloading all the registered plugins');
    UserPreferenceService.userLevelPreferenceChanged = true;
    this.userPreferenceService.saveAllUserPreferences();
    for (const callback of this.desktopIntegrationCleanupCallbacks) {
      if (null !== callback && undefined !== callback) {
        callback();
      }
    }
  }

  private redirectToLogin() {
    window.location.replace('/login' + this.utilService.urlParams());
  }

  private cleanupSubscriptions() {
    this.realtimeService.closeSocket();
    this.messageService.unsubscribeAll();
    this.ssePatientImagingHistoryService.clearSubscriptions();
    this.eventService.clearEventSubscriptions();
  }
}
